import {
  DataModuleSettingsDto,
  DataModuleUsageDto,
  ParamAuditLogDto,
  ParamRuleDto,
  ParamTableFieldDto
} from './parametre-donnees-api.models';
import {
  ChampFieldType,
  DonneesChampModel,
  DonneesHistoriqueRow,
  DonneesRegleModel,
  DonneesUtilisationFlags
} from './parametre-donnees.types';

function safeParse<T>(raw: unknown, fallback: T): T {
  if (raw == null || raw === '') {
    return fallback;
  }
  if (typeof raw === 'object') {
    return raw as unknown as T;
  }
  try {
    return JSON.parse(String(raw)) as T;
  } catch {
    return fallback;
  }
}

export function paramTableDtoToChamp(row: ParamTableFieldDto): DonneesChampModel {
  const opts = safeParse<Record<string, unknown>>(row.options_json, {});
  const liste = opts['liste'] as DonneesChampModel['liste'] | undefined;
  const relation = opts['relation'] as DonneesChampModel['relation'] | undefined;
  const nombre = opts['nombre'] as DonneesChampModel['nombre'] | undefined;

  const affichage = safeParse(row.display_json, defaultAffichage(row.ordre));
  const api = safeParse(row.api_json, defaultApi(row.champ_db));
  const utilisation = safeParse(row.usage_json, defaultUtilisation());

  return {
    id: String(row.id),
    ordre: row.ordre,
    code: row.champ_db,
    libelle: row.libelle_champ,
    description: row.description ?? '',
    type: (row.type || 'texte') as ChampFieldType,
    groupe: row.groupe ?? 'Général',
    placeholder: row.placeholder ?? '',
    valeurParDefaut: row.valeur_defaut ?? '',
    obligatoire: row.obligatoire,
    actif: row.actif,
    unique: row.unique_value,
    lectureSeule: row.lecture_seule,
    utiliseDans: row.utilise_dans ?? '—',
    liste,
    relation,
    nombre,
    affichage: { ...defaultAffichage(row.ordre), ...affichage },
    api: { ...defaultApi(row.champ_db), ...api },
    utilisation: { ...defaultUtilisation(), ...utilisation }
  };
}

function defaultAffichage(ordre: number): DonneesChampModel['affichage'] {
  return {
    afficherFiche: true,
    afficherListe: true,
    afficherFiltre: false,
    afficherRechercheAvancee: false,
    afficherImpression: true,
    afficherPos: false,
    afficherMobile: true,
    ongletCible: 'principal',
    sectionCible: 'general',
    ordreAffichage: ordre,
    largeurColonne: 'auto'
  };
}

function defaultApi(code: string): DonneesChampModel['api'] {
  return {
    exposeApi: true,
    nomTechniqueApi: code,
    lectureSeuleApi: false,
    autoriseCreation: true,
    autoriseMiseAJour: true,
    visibleWebhook: false
  };
}

function defaultUtilisation(): DonneesChampModel['utilisation'] {
  return {
    promoDeclenchement: false,
    promoExclusion: false,
    promoRegroupement: false,
    tarifRegles: false,
    tarifGenerationPrix: false,
    reportingBi: true,
    reportingExports: true,
    workflowValidations: false,
    workflowAutomatisations: false,
    rechercheSimple: true,
    rechercheAvancee: true
  };
}

export function champToParamTablePayload(
  m: DonneesChampModel,
  tableDbId: number,
  stationId: number,
  userId: number | null
): Partial<ParamTableFieldDto> & {
  table_db_id: number;
  station_id: number;
  user_id: number | null;
} {
  const options: Record<string, unknown> = {};
  if (m.liste) {
    options['liste'] = {
      monoOuMultiple: m.liste.monoOuMultiple,
      valeurs: m.liste.valeurs.map(({ label, value, couleur }) => ({ label, value, couleur }))
    };
  }
  if (m.relation) {
    options['relation'] = m.relation;
  }
  if (m.nombre) {
    options['nombre'] = m.nombre;
  }

  return {
    station_id: stationId,
    user_id: userId,
    table_db_id: tableDbId,
    champ_db: m.code,
    libelle_champ: m.libelle,
    type: m.type,
    ordre: m.ordre,
    groupe: m.groupe || null,
    description: m.description || null,
    placeholder: m.placeholder || null,
    valeur_defaut: m.valeurParDefaut || null,
    obligatoire: m.obligatoire,
    actif: m.actif,
    unique_value: m.unique,
    lecture_seule: m.lectureSeule,
    utilise_dans: m.utiliseDans || null,
    options_json: Object.keys(options).length ? options : null,
    display_json: m.affichage,
    api_json: m.api,
    usage_json: m.utilisation
  };
}

export function settingsDtoToGeneral(d: DataModuleSettingsDto): {
  champsPersonnalisesActifs: boolean;
  groupesActifs: boolean;
  tagsActifs: boolean;
  variantesActives: boolean;
  maxChampsPersonnalises: number;
  historisationModifications: boolean;
  filtresAvances: boolean;
} {
  return {
    champsPersonnalisesActifs: d.champs_perso_actifs,
    groupesActifs: d.groupes_actifs,
    tagsActifs: d.tags_actifs,
    variantesActives: d.variantes_actives,
    maxChampsPersonnalises: d.max_champs_perso,
    historisationModifications: d.historisation,
    filtresAvances: d.filtres_avances
  };
}

export function generalToSettingsDto(
  g: {
    champsPersonnalisesActifs: boolean;
    groupesActifs: boolean;
    tagsActifs: boolean;
    variantesActives: boolean;
    maxChampsPersonnalises: number;
    historisationModifications: boolean;
    filtresAvances: boolean;
  },
  stationId: number,
  moduleId: number
): DataModuleSettingsDto {
  return {
    station_id: stationId,
    module_id: moduleId,
    champs_perso_actifs: g.champsPersonnalisesActifs,
    groupes_actifs: g.groupesActifs,
    tags_actifs: g.tagsActifs,
    variantes_actives: g.variantesActives,
    max_champs_perso: g.maxChampsPersonnalises,
    historisation: g.historisationModifications,
    filtres_avances: g.filtresAvances
  };
}

export function usageDtoToFlags(d: DataModuleUsageDto): DonneesUtilisationFlags {
  return {
    promoDeclenchement: d.promo_declenchement,
    promoExclusion: d.promo_exclusion,
    promoRegroupement: d.promo_regroupement,
    tarifRegles: d.tarif_regles,
    tarifGenerationPrix: d.tarif_generation_prix,
    reportingBi: d.reporting_bi,
    reportingExports: d.reporting_exports,
    workflowValidations: d.workflow_validations,
    workflowAutomatisations: d.workflow_automatisations,
    rechercheSimple: d.recherche_simple,
    rechercheAvancee: d.recherche_avancee
  };
}

export function flagsToUsageDto(
  u: DonneesUtilisationFlags,
  stationId: number,
  moduleId: number
): DataModuleUsageDto {
  return {
    station_id: stationId,
    module_id: moduleId,
    promo_declenchement: u.promoDeclenchement,
    promo_exclusion: u.promoExclusion,
    promo_regroupement: u.promoRegroupement,
    tarif_regles: u.tarifRegles,
    tarif_generation_prix: u.tarifGenerationPrix,
    reporting_bi: u.reportingBi,
    reporting_exports: u.reportingExports,
    workflow_validations: u.workflowValidations,
    workflow_automatisations: u.workflowAutomatisations,
    recherche_simple: u.rechercheSimple,
    recherche_avancee: u.rechercheAvancee
  };
}

export function paramRuleDtoToModel(r: ParamRuleDto): DonneesRegleModel {
  return {
    id: String(r.id),
    champSource: r.champ_source,
    operateur: r.operateur,
    valeur: r.valeur_comparaison,
    action: r.action,
    champCible: r.champ_cible,
    statut: r.statut === 'inactive' ? 'inactive' : 'active',
    priorite: r.priorite,
    active: r.active
  };
}

export function regleToDto(
  r: DonneesRegleModel,
  stationId: number,
  tableDbId: number
): Partial<ParamRuleDto> & { station_id: number; table_db_id: number } {
  return {
    station_id: stationId,
    table_db_id: tableDbId,
    champ_source: r.champSource,
    operateur: r.operateur,
    valeur_comparaison: r.valeur,
    action: r.action,
    champ_cible: r.champCible,
    priorite: r.priorite,
    statut: r.statut,
    active: r.active
  };
}

export function auditDtoToRow(d: ParamAuditLogDto): DonneesHistoriqueRow {
  const action = d.action ?? d.ction ?? '';
  const hasLoggedAt = d.logged_at != null && String(d.logged_at).trim() !== '';
  const dateRaw = hasLoggedAt ? String(d.logged_at) : (d.date ?? '');
  return {
    id: String(d.id),
    date: dateRaw,
    utilisateur: String(d.user_id),
    action,
    objet: d.objet,
    ancienneValeur: d.ancienne_valeur ?? '',
    nouvelleValeur: d.nouvelle_valeur ?? '',
    source: d.source ?? ''
  };
}
